import { PROVIDER_API, USE_MOCK_AUTH, debtCustomerCasesPath } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import { displayPersonName } from "../utils/personName";
import { normalizeCase } from "./casesApi";

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function pickAmount(primary, fallback) {
  const first = toNumber(primary);
  if (first !== 0) return first;
  return toNumber(fallback);
}

export function normalizeDebtCustomer(raw) {
  if (!raw || typeof raw !== "object") return null;

  const nestedCase = raw.case && typeof raw.case === "object" ? raw.case : {};
  const name = displayPersonName(
    { first_name: raw.first_name, last_name: raw.last_name },
    raw.fullname,
    raw.customer_name,
    raw.customer,
    nestedCase.fullname,
    nestedCase.customer_name
  );
  const accountNumber =
    raw.account_number || nestedCase.account_number || raw.accountNumber || "";
  const phone =
    raw.phone_number || nestedCase.phone_number || raw.phone || raw.phoneNumber || "";

  const amountOwed = pickAmount(raw.total_amount_owed, nestedCase.amount_owed);
  const amountPaid = pickAmount(raw.total_amount_paid, nestedCase.amount_paid);
  const outstandingAmount =
    raw.total_outstanding != null && raw.total_outstanding !== ""
      ? toNumber(raw.total_outstanding)
      : nestedCase.outstanding != null && nestedCase.outstanding !== ""
        ? toNumber(nestedCase.outstanding)
        : Math.max(0, amountOwed - amountPaid);
  const daysOverdueRaw = raw.total_days_overdue ?? nestedCase.days_overdue ?? nestedCase.daysOverdue;
  const daysOverdue =
    daysOverdueRaw == null || daysOverdueRaw === ""
      ? 0
      : toNumber(daysOverdueRaw);

  return {
    id: String(raw.id ?? raw.debt_customer_slug ?? accountNumber ?? name),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    debt_customer_slug: raw.debt_customer_slug || (raw.id != null ? String(raw.id) : null),
    caseId: raw.case_id || nestedCase.case_id || null,
    accountNumber,
    first_name: raw.first_name || "",
    last_name: raw.last_name || "",
    fullname: raw.fullname || "",
    customer_name: name,
    name,
    phone,
    phone_number: phone,
    customer_number: raw.customer_number || nestedCase.customer_number || phone || "",
    email: raw.email || nestedCase.email || "",
    ghana_card_number: raw.ghanacard_id || raw.ghana_card_number || nestedCase.ghana_card_number || "",
    gender: raw.gender || nestedCase.gender || "",
    date_of_birth: raw.date_of_birth || nestedCase.date_of_birth || null,
    region: raw.region || nestedCase.region || "",
    country: raw.country || nestedCase.country || "",
    exact_area: raw.location || raw.exact_area || nestedCase.exact_area || "",
    gps_address: raw.gps_address || nestedCase.gps_address || "",
    loan_amount: toNumber(raw.loan_amount ?? nestedCase.loan_amount),
    dueDate: raw.due_date || nestedCase.due_date || null,
    last_payment_date: raw.last_payment_date || nestedCase.last_payment_date || null,
    cases_count: toNumber(raw.cases_count),
    amountOwed,
    amountPaid,
    outstandingAmount,
    daysOverdue,
    status: raw.status || nestedCase.status || "active",
    provider_id: raw.provider_id ?? null,
    institution: raw.provider_name || null,
    updatedAt: raw.updated_at || nestedCase.updated_at || raw.created_at || null,
    createdAt: raw.created_at || null,
  };
}

function extractCustomers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.debt_customers)) return data.debt_customers;
  if (Array.isArray(data?.customers)) return data.customers;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/**
 * GET /api/provs/debt-customers
 */
export async function fetchDebtCustomers(token, params = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const query = new URLSearchParams();
  query.set("page", String(params.page || 1));
  query.set("limit", String(params.limit || params.per_page || 15));
  query.set("sort_order", params.sort_order || "desc");
  if (params.search) query.set("search", params.search);

  const json = await apiRequest(`${PROVIDER_API.debtCustomers}?${query.toString()}`, {
    method: "GET",
    token,
  });
  const data = unwrapApiEnvelope(json);
  return {
    customers: extractCustomers(data).map(normalizeDebtCustomer).filter(Boolean),
    pagination: data?.pagination || null,
  };
}

let listInFlight = { token: null, promise: null };

/** Fetch every page so client-side search/filter still works. Dedupes concurrent callers (e.g. Strict Mode). */
export async function fetchAllDebtCustomers(token) {
  if (USE_MOCK_AUTH || !token) return [];

  if (listInFlight.token === token && listInFlight.promise) {
    return listInFlight.promise;
  }

  const promise = (async () => {
    const first = await fetchDebtCustomers(token, { page: 1, limit: 50 });
    if (!first) return [];

    const lastPage = Number(first.pagination?.last_page || 1);
    if (lastPage <= 1) return first.customers;

    const rest = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, i) =>
        fetchDebtCustomers(token, { page: i + 2, limit: 50 })
      )
    );
    return first.customers.concat(rest.flatMap((page) => page?.customers || []));
  })();

  listInFlight = { token, promise };
  try {
    return await promise;
  } finally {
    if (listInFlight.promise === promise) {
      listInFlight = { token: null, promise: null };
    }
  }
}

export const EMPTY_DEBT_CUSTOMERS_SUMMARY = {
  customers: 0,
  totalOwed: 0,
  totalPaid: 0,
  totalOutstanding: 0,
  avgDaysLate: 0,
  shareOfAllCustomers: 0,
};

export function normalizeDebtCustomersSummary(raw) {
  if (!raw || typeof raw !== "object") return { ...EMPTY_DEBT_CUSTOMERS_SUMMARY };
  return {
    customers: toNumber(raw.customers),
    totalOwed: toNumber(raw.total_owed ?? raw.totalOwed),
    totalPaid: toNumber(raw.total_paid ?? raw.totalPaid),
    totalOutstanding: toNumber(raw.total_outstanding ?? raw.totalOutstanding),
    avgDaysLate: toNumber(raw.avg_days_late ?? raw.avgDaysLate),
    shareOfAllCustomers: toNumber(
      raw.share_of_all_customers ?? raw.shareOfAllCustomers
    ),
  };
}

let summaryInFlight = { token: null, promise: null };

/**
 * GET /api/provs/debt-customers/summary
 */
export async function fetchDebtCustomersSummary(token) {
  if (USE_MOCK_AUTH || !token) return null;

  if (summaryInFlight.token === token && summaryInFlight.promise) {
    return summaryInFlight.promise;
  }

  const promise = (async () => {
    const json = await apiRequest(PROVIDER_API.debtCustomersSummary, {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    return normalizeDebtCustomersSummary(data);
  })();

  summaryInFlight = { token, promise };
  try {
    return await promise;
  } finally {
    if (summaryInFlight.promise === promise) {
      summaryInFlight = { token: null, promise: null };
    }
  }
}

const customerCasesInFlight = new Map();

export function clearDebtCustomersCache() {
  listInFlight = { token: null, promise: null };
  summaryInFlight = { token: null, promise: null };
  customerCasesInFlight.clear();
}

export function normalizeDebtCustomerCase(raw) {
  // Same row shape as the main Cases table so filtered views keep ID / days overdue / etc.
  return normalizeCase(raw);
}

function extractCases(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.cases)) return data.cases;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

/**
 * GET /api/provs/debt-customers/{debt_customer_slug}/cases
 */
export async function fetchDebtCustomerCases(token, debtCustomerSlug) {
  if (!debtCustomerSlug) return [];
  if (USE_MOCK_AUTH || !token) return null;

  const cacheKey = `${token}:${debtCustomerSlug}`;
  if (customerCasesInFlight.has(cacheKey)) {
    return customerCasesInFlight.get(cacheKey);
  }

  const promise = (async () => {
    const json = await apiRequest(debtCustomerCasesPath(debtCustomerSlug), {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    return extractCases(data).map(normalizeDebtCustomerCase).filter(Boolean);
  })();

  customerCasesInFlight.set(cacheKey, promise);
  return promise;
}
